import {
  useCallback,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { resolveAnchoredPopoverPosition } from './Cmarkdowntable.helpers';
import type {
  CmarkdowntablePopoverPosition,
  CmarkdowntablePopoverRect,
} from './Cmarkdowntable.types';

interface PopoverMeasurement {
  measuredWidth?: number;
  measuredHeight?: number;
}

interface UseCmarkdowntablePopoverPositionInput {
  activeAnchorRef: RefObject<HTMLDivElement | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  manualRectRef: RefObject<CmarkdowntablePopoverRect | null>;
  closePopover: () => void;
  setPopoverPosition: Dispatch<SetStateAction<CmarkdowntablePopoverPosition | null>>;
}

interface CmarkdowntablePopoverPositionController {
  updatePopoverPosition: () => void;
}

function resolveConnectedAnchor(anchorElement: HTMLDivElement | null): HTMLDivElement | null {
  if (!anchorElement || !anchorElement.isConnected) {
    return null;
  }

  return anchorElement;
}

function readPopoverMeasurement(popoverElement: HTMLDivElement | null): PopoverMeasurement {
  const measuredRect = popoverElement?.getBoundingClientRect();

  return {
    measuredWidth: measuredRect?.width,
    measuredHeight: measuredRect?.height,
  };
}

export function useCmarkdowntablePopoverPosition(
  input: UseCmarkdowntablePopoverPositionInput,
): CmarkdowntablePopoverPositionController {
  const {
    activeAnchorRef,
    popoverRef,
    manualRectRef,
    closePopover,
    setPopoverPosition,
  } = input;

  const updatePopoverPosition = useCallback((): void => {
    if (manualRectRef.current != null) {
      return;
    }

    const anchorElement = resolveConnectedAnchor(activeAnchorRef.current);
    if (!anchorElement) {
      closePopover();
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const measurement = readPopoverMeasurement(popoverRef.current);
    const nextPosition = resolveAnchoredPopoverPosition({
      anchorRect,
      measuredWidth: measurement.measuredWidth,
      measuredHeight: measurement.measuredHeight,
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight,
    });

    if (!nextPosition) {
      closePopover();
      return;
    }

    setPopoverPosition(nextPosition);
  }, [activeAnchorRef, closePopover, manualRectRef, popoverRef, setPopoverPosition]);

  return { updatePopoverPosition };
}
